export default {
  title: 'Settings',
  organization: 'Organization',
  personalGroup: 'Personal',
  aiGroup: 'CARTO AI',
  personalSettings: {
    usage: {
      overview: 'Overview',
      consumedLabel: 'You have consumed',
      intro:
        'This is your own consumption and the personal limits your organization administrator has set for you. Usage quota figures update once a day. Consumption resets at the start of each contract year, next on {resetDate}.',
      introNoReset:
        'This is your own consumption and the personal limits your organization administrator has set for you. Usage quota figures update once a day. Consumption resets at the start of each contract year.',
      learnMore: 'Learn more about quotas',
      loadError: "We couldn't load your usage right now. Please try again in a moment.",
      usageUnavailable: "We couldn't load your usage consumption right now. Please try again in a moment.",
      reached: 'You have reached this quota. Contact your organization administrator to request a higher limit.',
      quotaTypes: { usage: 'usage', lds: 'LDS', ai: 'AI' },
      groupLimits: {
        nearTitle: 'A group you belong to is close to a shared limit',
        reachedTitle: 'A group you belong to has reached a shared limit',
        nearBody:
          'The "{group}" group has used {consumed} of {limit} for its shared {type} quota. This limit is shared across everyone in the group, and your own usage counts toward it. If the group reaches the limit, you will lose access to the CARTO Platform and APIs.',
        reachedBody:
          'The "{group}" group has reached its shared {type} quota of {limit}. As a member of this group, you can no longer use the CARTO Platform and APIs. To increase the quota, contact your organization administrator.'
      },
      usageQuota: {
        title: 'Usage Quota',
        summary: '{current} of {limit} usage quota units',
        summaryNoLimit: '{current} usage quota units',
        description:
          '<docsLink>Usage quota units</docsLink> are consumed every time you interact with the CARTO Platform and APIs, excluding metadata. These figures reflect your own individual consumption, not your whole organization. The Usage quota will reset on {resetDate}. If you need more quota, contact your organization administrator.'
      },
      lds: {
        title: 'Location Data Services (LDS)',
        creditsOf: '{current} / {max} credits',
        unlimited: '{current} credits (no limit)',
        description: 'Credits for geocoding, isoline, and routing calculations.'
      },
      ai: {
        title: 'AI quota',
        tokensOf: '{current} of {max} tokens',
        unlimited: '{current} tokens (no limit)',
        description:
          'Tokens across all AI models — CARTO-managed and any custom models configured in your organization.'
      }
    },
    general: {
      profile: {
        title: 'Profile',
        authMethod: 'Signs in with {method}',
        authMethods: {
          google: 'Google',
          github: 'GitHub',
          samlp: 'Single Sign-On',
          auth0: 'email and password'
        }
      },
      password: {
        title: 'Password',
        description: 'Change the password you use to sign in to CARTO.',
        change: 'Change password',
        dialog: {
          title: 'Change password',
          body: 'CARTO will email you a link to change your password. Follow the link in that email to set a new one.',
          submit: 'Email me a link',
          sent: "We've sent you an email with a link to change your password. Check your inbox.",
          done: 'Done',
          error: "We couldn't send the email. Please try again."
        }
      },
      delete: {
        title: 'Delete user',
        warning:
          'Permanently delete your CARTO user. This removes your profile and revokes your access to this organization. This action cannot be undone.',
        button: 'Delete my user',
        dialog: {
          title: 'Delete your user?',
          body: "This permanently deletes your CARTO user and revokes your access to this organization. Any maps, workflows and connections you own are transferred to the organization's default admin. This action cannot be undone.",
          confirmLabel: 'Type your email to confirm',
          cancel: 'Cancel',
          submit: 'Delete user'
        }
      }
    }
  },
  isBeingCreated: {
    title: 'Workspace',
    description: 'Your organization is being set up, the process will take around 30 seconds, please wait.'
  },
  wrongTenant: {
    title: 'Sign in at {tenantDomain}',
    description:
      'You signed in as <strong>{email}</strong>. Your organization is on <strong>{tenantDomain}</strong>, not here. Go there to continue, or log out to use a different account.',
    goToTenant: 'Go to {tenantDomain}',
    logout: 'Log out'
  },
  sfConnectDialog: {
    title: 'Welcome to CARTO',
    headlineOne: 'CARTO is now connected to Snowflake!',
    paragraphOne:
      'Go ahead and unlock the power of spatial analysis with CARTO, using your own Snowflake data. From now on, you can always go back to CARTO through Snowflake Partner Connect.',
    headlineTwo: 'Here are some resources to get you started:',
    docs: {
      first: 'Documentation: CARTO Analytics Toolbox for Snowflake.',
      second: 'Demo: Locating optimal sites for 15 minute grocery delivery.',
      third: 'Webinar: How to use CARTO with Snowflake.'
    }
  },
  sections: {
    general: 'General',
    personalGeneral: 'Profile',
    myUsageAndQuotas: 'Usage',
    mcpServer: 'CARTO MCP Server',
    aiAnalytics: 'Analytics',
    usageWithoutActivity: 'Usage & Quotas',
    usage: 'Quotas & Activity',
    subscriptions: 'Subscription',
    apps: 'Registered apps',
    users: 'Users & Groups',
    appearance: 'Customizations',
    ai: 'CARTO AI',
    aiConfiguration: 'Configuration',
    analytics: 'Analytics Toolbox',
    advanced: 'Advanced',
    assetManagement: 'Asset management',
    dataObservatory: 'Data Observatory',
    governance: 'Governance',
    authenticationAndSSO: 'Authentication & SSO'
  },
  cards: {
    general: {
      title: 'Overview',
      accountName: 'Organization name',
      accountId: 'Organization ID',
      version: 'Version',
      cartoCloudVersion: 'CARTO Cloud',
      cartoSelfHostedVersion: 'Self-hosted',
      rename: 'Edit name',
      renameModal: {
        title: 'Rename organization',
        invalid: 'Invalid name',
        cancel: 'Cancel',
        submit: 'Rename'
      },
      successMessage: 'The organization was renamed correctly',
      billing: {
        title: 'Billing'
      }
    },
    governance: {
      connections: {
        title: 'Allowed connection methods',
        description:
          'Select which Data Warehouse providers and connection methods your users can create connections with. Only the enabled methods will be available in your organization.',
        signInWithGoogle: 'Sign-in with Google',
        serviceAccount: 'Service Account',
        workloadIdentityFederation: 'Workload Identity Federation',
        usernamePassword: 'Username/Password',
        keyPair: 'Key Pair',
        oauth: 'OAuth',
        personalAccessToken: 'Personal Access Token',
        oauthU2M: 'OAuth U2M',
        oauthM2M: 'OAuth M2M',
        wallet: 'Wallet',
        permission: {
          title: 'Create connections',
          label: 'Allow editors to create connections',
          description:
            'Allow Editors in your organization to create new Data Warehouse connections using the methods enabled above.'
        },
        cartoDataWarehouse: {
          title: 'CARTO Data Warehouse',
          label: 'Enable the CARTO Data Warehouse',
          description:
            'Every CARTO organization comes provisioned with an active CARTO Data Warehouse connection by default. Allow users in your organization to use it.'
        },
        sharing: {
          title: 'Connection sharing',
          label: 'Allow users to share their connections',
          description:
            "Controls sharing of connections that use the owner's stored credentials — with the whole organization, specific groups, or individual people. Connections that require each viewer to provide their own credentials can always be shared, even when this is off."
        }
      },
      maps: {
        publicMaps: {
          title: 'Public maps',
          description: 'Let Editors create public maps, accesible to anyone with the URL.',
          label: 'Allow users to create public maps'
        },
        downloadPdf: {
          title: 'Download Report',
          description: 'Let Editors enable downloading PDF reports in their maps.',
          label: 'Allow users to download PDF reports'
        },
        exportingViewportData: {
          title: 'Export viewport data',
          description: 'Let Editors enable exporting viewport data in their maps.',
          label: 'Allow users to export viewport data'
        },
        exportMap: {
          title: 'Export map',
          description: 'Let Editors enable exporting the current map view as a high-resolution PNG.',
          label: 'Allow users to export the current map view as a high-resolution PNG.'
        }
      },
      workflows: {
        title: 'Workflows'
      }
    },
    authenticationAndSSO: {
      autoJoin: {
        title: 'Auto-join',
        enable: 'Enable auto-join'
      }
    },
    stats: {
      chart: {
        mapLoads: { label: 'Map loads', description: '' },
        apps: {
          label: 'Applications',
          description:
            'Maximum number of applications you can create in the Developers section, for authentication in the CARTO APIs.'
        },
        tokens: {
          label: 'API Access Tokens',
          description: 'Number of API Access Tokens created in the Workspace or with the Tokens API.'
        },
        usage: {
          label: 'Usage quota',
          descriptionWithoutActivity:
            'Usage Quota units are consumed every time a user interacts with the CARTO Platform and APIs, excluding metadata. The Usage Quota is annual and it will reset on {date}.',
          description:
            'Usage Quota units are consumed every time a user interacts with the CARTO Platform and APIs, excluding metadata. The Usage Quota is annual and it will reset on {date}. You can find a breakdown of the usage quota by day, user or API in the <a>Activity data</a>.'
        },
        usageOldPricing: {
          label: 'Usage quota',
          description:
            'Usage Quota units are consumed every time a user interacts with the CARTO Platform and APIs, excluding metadata. This quota is annual and does not apply. It is purely informative for your organization.'
        },
        usagePerMonth: {
          label: 'Average monthly usage',
          alertText: {
            success:
              'At the current usage level, your organization should have enough quota for the remainder of the subscription.',
            warning:
              'At the current usage level, your organization doesn’t have enough quota for the remainder of the subscription. If you need to increase your quota, please contact your Account Manager, Customer Success Manager or send an email to <link>support@carto.com</link>',
            warningExceeded:
              'Your organization has consumed all the quota allocated for your subscription. If you need to increase your quota, please contact your Account Manager, Customer Success Manager or send an email to <link>support@carto.com</link>',
            paygCapReached:
              "Your plan's monthly usage limit has been reached. Consumption-billable operations are paused until the next billing period. To raise the limit, contact your Account Manager, Customer Success Manager or email <link>support@carto.com</link>.",
            warningButton: 'Learn more'
          }
        },
        maps: { label: 'Total maps', description: 'Total number of maps in your CARTO organization' },
        mapsPublic: {
          label: 'Public maps',
          description: 'Total number of maps shared in Public mode in your CARTO organization'
        },
        connections: {
          label: 'Connections',
          description: 'Total number of connections created in your CARTO organization'
        },
        users: { label: 'Users', description: '' },
        editorUsers: {
          label: 'Editor users',
          description:
            'Users that can create and edit maps, connections, and other content. Admins also use an Editor seat.'
        },
        viewerUsers: {
          label: 'Viewer users',
          description: 'Users that can view maps and applications created by Editors'
        },
        geocode: {
          label: 'Geocoding / month',
          description: 'LDS credits are reset on the first day of the month'
        },
        lds: {
          label: 'Geocoding and Isolines/year',
          description: 'LDS credits are annual and they will reset on {date}.'
        },
        isolines: {
          label: 'Isolines / month',
          description: 'LDS credits are reset on the first day of the month'
        },
        indicator: '{current} of {max}',
        errorMessage:
          "There was an error loading the organization's stats. Please try again later and contact us if the error persists."
      }
    },
    usage: {
      analytics: {
        tabTitle: 'Analytics',
        sectionTitle: 'Usage quota analytics',
        exportNote: 'For deeper or custom analysis, use the Activity Data export.',
        timeRange: {
          ariaLabel: 'Time range'
        },
        ranges: {
          cycle: 'Current cycle',
          '1M': '1M',
          '3M': '3M',
          '1Y': '1Y'
        },
        serviceBreakdown: {
          title: 'Usage Quota by API / service'
        },
        chart: {
          total: 'Total',
          cumulative: 'Cumulative',
          cumulativeDisabledHint: 'Clear the time filter to switch to the cumulative view'
        },
        service: {
          sql: 'SQL API',
          import: 'Import API',
          maps: 'Maps API',
          lds: 'LDS API',
          ai: 'AI',
          widgets: 'SQL API (Widgets)'
        },
        sections: {
          byUser: 'Top consumers by user',
          byMap: 'Top consumers by map',
          byWorkflow: 'Top consumers by workflow',
          byClientId: 'Top consumers by client'
        },
        columns: {
          user: 'User',
          map: 'Map',
          workflow: 'Workflow',
          clientId: 'Client',
          owner: 'Owned by',
          consumption: 'Consumption'
        },
        search: {
          userPlaceholder: 'Search users by email',
          mapPlaceholder: 'Search maps by name',
          workflowPlaceholder: 'Search workflows by name',
          clientPlaceholder: 'Search clients by name'
        },
        // First-party request origins (commons' MetricClient vocabulary). Product
        // names, kept here so the parenthetical qualifiers are translatable.
        clients: {
          // Deliberately not "Other": this table also aggregates its long tail
          // into an "Others (N)" row, and the two must not read as the same thing.
          other: 'Untagged requests',
          builder: 'Builder',
          workspace: 'Workspace',
          workflows: 'Workflows',
          'workflows-ui': 'Workflows (editor)',
          'workflows-api-sync': 'Workflows (API sync)',
          'workflows-api-async': 'Workflows (API async)',
          'workflows-mcptool-sync': 'Workflows (MCP tool sync)',
          'workflows-mcptool-async': 'Workflows (MCP tool async)',
          'workflows-viewer': 'Workflows (viewer)',
          'workflows-schedule': 'Workflows (scheduled)',
          c4react: 'CARTO for React',
          c4deck: 'CARTO for deck.gl',
          'carto-qgis-plugin': 'CARTO QGIS Plugin',
          siteselection: 'Site Selection',
          cpg: 'CPG',
          ooh: 'Out of Home',
          'consumption-tracker': 'Consumption Tracker'
        },
        publicTag: 'Public',
        deletedLabel: 'Deleted',
        unavailableLabel: 'Unavailable',
        othersLabel: 'Others ({count})',
        othersDialogTitle: 'Other clients',
        units: {
          unitsValue: '{value} units'
        },
        error: {
          description: 'We could not load usage analytics. Please try again later.'
        },
        degraded: {
          banner:
            'Usage data is temporarily unavailable. Consumption may show as zero until the connection recovers — please check back later.'
        },
        namesError: {
          description: 'Names cannot be loaded right now, so consumption is shown without them.',
          retry: 'Retry'
        },
        empty: {
          serviceTitle: 'No usage in this period',
          serviceDescription: 'There is no recorded consumption for the selected time range.',
          tableTitle: 'No consumption to show',
          tableDescription: 'No consumers recorded for the selected time range.',
          searchTitle: 'No matches',
          searchDescription: 'No consumers match your search in this period.',
          degradedTitle: 'Data temporarily unavailable',
          degradedDescription: 'Consumption cannot be retrieved right now. Please check back later.'
        }
      },
      timeline: {
        title: 'Units consumed',
        summary: '{current} of {maxQuota}',
        tooltip: {
          daily: 'Daily',
          cumulative: 'Cumulative'
        }
      },
      quotas: {
        title: 'Quotas Overview',
        learnMore: {
          title: 'Learn more',
          content: 'Find more information and understand how these quotas are calculated.',
          button: 'Check our documentation'
        }
      },
      quotasOverview: {
        title: 'Quotas Overview',
        manageQuotaLimits: 'Manage quota limits',
        totalConsumed: 'Total consumed',
        usageQuota: 'Usage Quota',
        'usageQuota.description':
          '<docsLink>Usage quota units</docsLink> are consumed every time a user interacts with the CARTO Platform and APIs, excluding metadata. The Usage quota will reset on {resetDate}. Explore detailed consumption using the <activityLink>Activity Data</activityLink>.',
        locationDataServices: 'Location Data Services (LDS)',
        'locationDataServices.description':
          'Credit quota for geocoding, isoline, and routing calculations. LDS quota will reset on {resetDate}.',
        aiQuota: 'AI quota',
        'aiQuota.allProviders': 'All AI providers',
        'aiQuota.cartoManaged': 'CARTO-managed models',
        'aiQuota.tokensValue': '{value} tokens',
        'aiQuota.tokensOf': '{current} of {max} tokens',
        'aiQuota.description':
          'Token quota for CARTO-managed models used in your organization. This quota will reset on {resetDate}.',
        tokens: 'API Access Tokens',
        'tokens.description':
          'Permanent, granular authentication tokens used in CARTO. <link>Go to Asset management</link>',
        tokensCreated: 'API Access Tokens created',
        editorUsers: 'Editor users',
        'editorUsers.description':
          'Users that can create and edit maps, connections, and other content. Admins also use an Editor seat. <link>Go to Users</link>',
        viewerUsers: 'Viewer users',
        'viewerUsers.description': 'Users that can only view. Includes Guest users. <link>Go to Users</link>',
        seats: 'Seats assigned',
        unlimited: 'Unlimited',
        users: 'Users',
        totalUsers: 'Total users',
        'unlimited.users.description': 'Your plan includes unlimited users. <link>Go to Users</link>'
      },
      quotaLimits: {
        title: 'Quota Limits',
        lastUpdated: 'Last updated {lastUpdated}.',
        lastUpdated24h: 'It may take up to 24 hours to reflect usage. <link>More info.</link>',
        tabs: {
          usage: 'Usage quota',
          lds: 'LDS quota',
          ai: 'AI quota',
          apiAccessTokens: 'API Access Tokens',
          'usage.description': 'Set quota limits for usage consumption per user or group.',
          'lds.description': 'Set quota limits for Location Data Services (LDS) per user or group.',
          'ai.description': 'Set quota limits for AI consumption per user or group.',
          'apiAccessTokens.description': 'Set quota limits for API Access Tokens per user or group.'
        },
        setButton: 'Set {quotaType} limit',
        toBeCalculated: 'To be calculated',
        dialog: {
          title: 'Set {quotaType} limit',
          editTitle: 'Edit {quotaType} limit',
          description: 'Define a {quotaType} limit for a specific user or group.',
          descriptionAi: 'This covers both CARTO-managed models and custom models.',
          editDescription: 'Edit the {quotaType} limit for {targetName}.',
          label: 'Select user or group',
          searchLabel: 'Search for a user or group',
          searchPlaceholder: 'Type the user or group name',
          limitLabel: '{quotaType} limit',
          limitTooltip: 'The maximum amount of quota that can be consumed by this user or group.',
          sliderAriaLabel: 'Quota limit slider',
          saveButton: 'Save',
          updateButton: 'Save changes',
          alertContent: 'The quota consumption will reset on {resetDate}. Learn more in our <link>documentation</link>',
          warningAi: {
            user: {
              titleOk:
                'This is a hard limit. Once the user reaches the quota limit, they will be blocked from using any CARTO-managed models or custom AI models.',
              titleExceeded:
                'This user has already consumed {currentConsumption} tokens, reaching the quota limit. The user is blocked from using any CARTO-managed models or custom AI models.',
              titleWarning:
                'This user has already consumed {currentConsumption} tokens. Setting this quota limit will block them from using any CARTO-managed models or custom AI models.'
            },
            group: {
              titleOk:
                "A group's AI usage is the sum of all its members' token consumption. Once the group reaches its quota limit, all its members will be blocked from using any CARTO-managed models or custom AI models.",
              titleExceeded:
                "This group has already consumed {currentConsumption} tokens, reaching the quota limit. The group's members are blocked from using any CARTO-managed models or custom AI models.",
              titleWarning:
                'This group has already consumed {currentConsumption} tokens. Setting this quota limit will block all members from using any CARTO-managed models or custom AI models.'
            }
          },
          warningLds: {
            user: {
              titleOk:
                'This is a hard limit. Once the user reaches the quota limit, they will not be able to perform any geocoding, isoline, or routing calculations.',
              titleExceeded:
                'This user has already consumed {currentConsumption} LDS credits, reaching the quota limit. The user is blocked from geocoding, isoline, or routing calculations.',
              titleWarning:
                'This user has already consumed {currentConsumption} LDS credits. Setting this quota limit will block them from geocoding, isoline, or routing calculations.'
            },
            group: {
              titleOk:
                "A group's LDS usage is the sum of all its members' consumption. Once the group reaches its quota limit, all its members will be blocked from geocoding, isoline, or routing calculations.",
              titleExceeded:
                "This group has already consumed {currentConsumption} LDS credits, reaching the quota limit. The group's members are blocked from geocoding, isoline, or routing calculations.",
              titleWarning:
                'This group has already consumed {currentConsumption} LDS credits. Setting this quota limit will block all members from geocoding, isoline, or routing calculations.'
            }
          },
          warning: {
            user: {
              titleOk:
                'This is a hard limit. Once the user reaches the quota limit, they will be blocked from further actions.',
              titleExceeded:
                'This user has already consumed {currentConsumption} units, reaching the quota limit. The user is blocked from using the CARTO Platform and APIs.',
              titleWarning:
                'This user has already consumed {currentConsumption} units. Setting this quota limit will block their access to the CARTO Platform and APIs.'
            },
            group: {
              titleOk:
                "A group's usage is the sum of all its members' consumption. Once the group reaches its quota limit, all its members will be blocked from using the CARTO Platform and APIs.",
              titleExceeded:
                "This group has already consumed {currentConsumption} units, reaching the quota limit. The group's members are blocked from using the CARTO Platform and APIs.",
              titleWarning:
                'This user has already consumed {currentConsumption} units. Setting this quota limit will block all members from using the CARTO Platform and APIs.'
            },
            userCurrentConsumption: "User's current consumption: {currentConsumption}",
            organizationTotalQuotaLimit: "Organization's total quota limit: {maxQuota}"
          },
          'selector.disabledTooltip': 'This {type} already has a quota limit set'
        },
        emptyState: {
          title: "You haven't set any {quotaType} limits.",
          description: 'Set limits to restrict quota consumption for specific users and groups.',
          createButton: 'Set {quotaType} limit',
          or: 'or',
          learnMore: 'Learn more'
        },
        notifications: {
          createSuccess: 'Quota limit created successfully',
          createError: 'Failed to create quota limit',
          updateSuccess: 'Quota limit changed successfully',
          updateError: 'Failed to update quota limit',
          deleteSuccess: 'Quota limit deleted successfully',
          deleteError: 'Failed to delete quota limit'
        },
        table: {
          columns: {
            userGroup: 'User/Group',
            role: 'Role',
            quotaLimit: 'Quota limit',
            quotaConsumed: 'Quota consumed'
          },
          actions: {
            edit: 'Edit quota limit',
            delete: 'Delete'
          },
          empty: 'No quota limits found',
          emptyResults: 'No results were found'
        },
        filters: {
          searchPlaceholder: 'Search by email or group name',
          targetTypeAll: 'All types',
          targetTypeUser: 'User',
          targetTypeGroup: 'Group'
        },
        exceededAlert: {
          title: '{count, plural, one {# quota limit exceeded} other {# quota limits exceeded}}',
          description:
            '{count, plural, one {# user or group} other {# users or groups}} have reached their quota limits.'
        },
        deleteDialog: {
          title: 'Delete {quotaType} quota limit',
          description: 'Are you sure you want to delete the {quotaType} quota limit for the user {targetName}?',
          warning: 'This action cannot be undone.',
          confirmButton: 'Yes, delete'
        }
      },
      activityData: {
        title: 'Activity Data',
        content: {
          title: 'Export CARTO activity data',
          description:
            'Activity data tracks all user actions and API usage in your CARTO organization. Administrators can export it for custom analysis, CARTO activity reporting, quota monitoring, or as security audit logs.',
          exportButton: 'Export CARTO activity data',
          apiAccess: {
            title: 'Access via API',
            description:
              'You can access CARTO activity data programmatically via API, which allows for seamless integration of this data into your existing dashboards and tools.',
            learnMore: 'Learn more',
            tooltip: 'Only available for Medium license plans or higher'
          },
          upgrade: {
            title: 'Upgrade your plan',
            message:
              "Exporting activity data from your CARTO organization requires a larger Enterprise plan. Let us know if you're interested and we'll guide you through the process."
          }
        },
        form: {
          title: 'Export CARTO activity data',
          description:
            'Export data containing all user actions and API usage in your CARTO organization, for the date range selected below.',
          fields: {
            from: 'From',
            to: 'To',
            format: 'File format',
            formats: {
              csv: 'CSV',
              parquet: 'Parquet'
            }
          },
          errors: {
            endBeforeStart: 'End date must be after start date',
            startBeforeAccountCreation: "There's no data before your CARTO organization was created",
            rangeExceeded: 'Each export can contain up to 60 days of data',
            futureDate: 'Date cannot be today or a future date'
          },
          info: 'Each export can contain up to 60 days of data. Dates are calculated using UTC',
          button: 'Export data'
        }
      },
      title: 'Subscription',
      quotaTitle: 'Quota',
      detailsTitle: 'Details',
      usersTitle: 'Users',
      featuresTitle: 'Features',
      usageTitle: 'Usage',
      cartoForDevelopers: 'Developers',
      locationDataServices: 'Location Data Services (LDS)',
      items: {
        domain: 'Domain',
        expires: 'Expires',
        billingInformation: 'Billing information',
        manageBillingInformation: 'Manage billing',
        trial: 'Trial',
        plan: 'Current plan',
        enterpriseSSO: 'Current status',
        mfa: 'Multi Factor Authentication',
        region: 'Region'
      }
    },
    users: {
      title: 'Users',
      inviteUsersDialog: {
        title: 'Invite user',
        content: 'Invite new members by email to join your organization',
        inputPlaceholder: 'name@email.com',
        inputLabel: 'Email address',
        invite: 'Invite',
        successMessage: 'Invitation correctly sent',
        role: 'User role'
      },
      invitations: {
        title: 'Invitations',
        notFound: 'No pending invitations',
        searchPlaceholder: 'Search invitations',
        status: {
          sent: 'Invitation sent',
          expired: 'Invitation expired'
        },
        expiredTooltip:
          'Invitations are valid for 7 days. Re-send this invitation using the three-dot menu to make it valid again',
        actions: {
          resend: 'Resend invitation',
          withdraw: 'Withdraw it'
        },
        sendDialog: {
          title: 'Invite users',
          content: 'Send invitations via email so these users can join your organization.',
          submit: 'Invite',
          success:
            'If the emails are valid and the users have not already been invited or registered, they will receive an email',
          inputPlaceholder: 'user1@email.com, user2@gmail.com',
          inputLabel: 'Email addresses',
          inputHint: 'You can invite multiple email addresses, separated by comma.',
          role: 'Role',
          alreadyInvitedError: 'Some emails have been already invited, please check above',
          alreadyJoinedError: 'Some emails have been already registered, please check above',
          multipleErrors:
            "Some of the addresses can't be invited because they are invalid, already registered or invited, or other reasons. Please review and try again."
        },
        resendDialog: {
          title: 'Resend invitation',
          content: 'Are you sure you want to resend {email} an invitation to join your organization?',
          submit: 'Yes, resend',
          success: 'Invitation correctly resent',
          error: 'The invitation could not be resent. Please try again or contact support if the issue persists.'
        },
        withdrawDialog: {
          title: 'Withdraw invitation',
          content_1: 'Are you sure you want to withdraw the invitation sent to {email}?',
          content_2: "They won't be able to join your organization using the previous invite.",
          submit: 'Withdraw invitation',
          success: 'Invitation correctly withdrawn',
          error: "The invitation couldn't be withdrawn. Please try again or contact support if the issue persists."
        }
      },
      requests: {
        title: 'Requests',
        notFound: 'No pending requests',
        searchPlaceholder: 'Search requests',
        status: {
          approved: 'Request approved',
          pending: 'Request pending',
          expired: 'Request expired'
        },
        expiredTooltip:
          'This request has expired. You can send an invitation to this user, or they can submit another request to join at any time',
        actions: {
          approve: 'Approve request',
          decline: 'Decline it'
        },
        approveDialog: {
          title: 'Approve request',
          content: 'Are you sure you want to approve {email} request to join your organization?',
          submit: 'Yes, approve',
          success: 'Request correctly approved',
          error: 'The request could not be approved. Please try again or contact support if the issue persists.'
        },
        declineDialog: {
          title: 'Decline request',
          content_1: 'Are you sure you want to decline the request sent by {email}?',
          content_2: "They won't be able to join your organization using the previous request.",
          submit: 'Decline request',
          success: 'Request correctly declined',
          error: "The request couldn't be declined. Please try again or contact support if the issue persists."
        }
      },
      sso: {
        tab: 'SSO',
        title: 'Single Sign-On (SSO)',
        message:
          'Users can authenticate to this CARTO organization using a Single Sign-On (SSO) integration, allowing them to log in with their company credentials. This enhances security and simplifies the onboarding process.',
        url: 'URL SSO',
        urlCopied: 'URL copied to clipboard',
        urlMessage:
          'This URL grants access to the organization using your company Single Sign-On. You can send this URL to your teammates if you want them to join this CARTO organization.',
        roleManagement: 'Role management with SSO',
        info: {
          lookingEnable: {
            title: 'Looking to enable Single Sign-On?',
            message:
              "Let us know if you want to start using SSO to authenticate your users and we'll guide you through the process."
          },
          upgrade: {
            title: 'Upgrade your plan',
            message:
              "Single Sign-On requires a larger Enterprise plan. Let us know if you want to start using SSO to authenticate your users and we'll guide you through the process."
          }
        },
        enforcedLoginMethod: {
          label: 'Enforce Single Sign-On as the only authentication method',
          helperText:
            'When enabled, users in your organization will only be able to login using Single Sign-On. Other authentication mechanisms will be disabled. <a>More info</a>',
          disabledTooltipText:
            'The organization must have at least one Admin using SSO authentication. <a>More info</a>',
          success: 'Successfully updated "Enforce Single Sign-On" settting.',
          error: 'Failed to update "Enforce Single Sign-On" settting.',
          confirmDialog: {
            title: 'Enforce SSO login',
            subtitle: 'Are you sure you want to enforce SSO in your organization?',
            confirmLabel: 'Yes, enforce SSO',
            alertContent:
              'There are {num} users in your organization using other authentication methods. These users will not be able to log in if enforce SSO is enabled'
          }
        },
        jitProvisioning: {
          label: 'Enable Just-in-Time provisioning',
          helperText:
            'Allows users accessing with SSO to register in CARTO without going through the Signup form. <a>More info</a>',
          success: 'Successfully updated Just-in-Time settting of an account.',
          error: 'Failed to update Just-in-Time settting of an account.'
        },
        groupsRoleManagement: {
          superadminInYourOrgMessage:
            'Group-role Mapping is disabled since there is a Superadmin user in your organization. If you wish to enable this, please contact Support to get the process started.',
          label: 'Enable role management by groups',
          noGroupsHelperText:
            'You need to have at least one group before enabling role management by groups. Please create one in the Groups tab.',
          helperText:
            'You will be mapping your SSO groups to roles, and users will be automatically granted the highest-privileged role from the groups they belong to.'
        },
        defaultAdmin: {
          title: 'Default admin',
          description:
            "The organization's default Admin will inherit assets (maps, workflows, etc) from removed or downgraded users.",
          label: 'Default Admin inheritor',
          success: 'Default admin successfully updated.',
          error: 'Failed to update the default admin.'
        },
        scim: {
          title: 'SCIM',
          currentStatus: 'Current status',
          info: {
            lookingEnable: {
              title: 'Looking to enable SCIM?',
              message:
                "Let us know if you want to start using SCIM to ensure your users and groups are always synchronised with your SSO IDP, and we'll guide you through the process."
            }
          }
        }
      },
      groups: {
        info: {
          lookingEnable: {
            title: 'Looking to enable user groups?',
            message:
              "Use CARTO at scale with fully synchronized groups coming from your SSO integration, that your users will leverage for granular sharing and collaboration. Let us know if you want to enable Groups and we'll guide you through the process."
          },
          upgrade: {
            title: 'Looking to enable user groups?',
            message:
              "Use CARTO at scale with fully synchronized groups coming from your SSO integration, that your users will leverage for granular sharing and collaboration. In order to use Groups you will need to upgrade your plan. Let us know and we'll guide you through the process."
          }
        }
      },
      dontRequireJoinApproval: 'Let anyone with a @{emailDomain} email join this organization.',
      table: {
        headCell: {
          users: 'User',
          role: 'Role',
          authMethod: 'Authentication method',
          status: 'Status',
          added: 'Added',
          invited: 'Invited'
        }
      },
      roleFilters: {
        allUsers: 'All users',
        allGroups: 'All groups',
        allRoles: 'All roles',
        admins: 'Admins',
        builders: 'Editors',
        viewers: 'Viewers',
        superadmins: 'Superadmins',
        guests: 'Guest viewers',
        guestsDescription: 'Can only view maps explicitly shared with them'
      },
      wouldExceedQuotaModal: {
        quotaTitle: '{capitalizedRole} users',
        alertMessage:
          'You can’t add any more {role} users with your current plan. Free up a seat by modifying or removing other users, or request to be contacted for an upgrade.',
        invite: {
          title: "Couldn't send the invite",
          message: 'Check the exceeded quotas below before inviting <strong>{email}</strong> as a {role} user.'
        },
        assignRole: {
          title: "Couldn't assign user role",
          message: 'Check the exceeded quotas below before assigning <strong>{email}</strong> as a {role} user.'
        },
        moreInfo: {
          title: "Can't add new {role} users",
          message: 'Check the exceeded quotas below before inviting a user or changing their roles.'
        }
      },
      actions: {
        delete: 'Delete',
        makeEditor: 'Make Editor',
        makeAdmin: 'Make Admin',
        makeViewer: 'Make Viewer',
        makeSuperadmin: 'Make Superadmin',
        makeGuest: 'Make Guest Viewer'
      },
      deleteUsersDialog: {
        content: 'Are you sure you want to delete {email} from the organization? This action cannot be undone.',
        title: 'Delete user',
        info: 'All maps, workflows, projects, connections and credentials from {userName} must be transferred to another user. M2M OAuth Clients owned by this user will be deleted for security reasons, and any projects using them will stop working. We recommend replacing them before deleting or downgrading this user',
        newAssetOwnerLabel: 'New asset owner:',
        newAssetOwnerPlaceholder: 'Select user',
        newAssetOwnerInfo: 'Assets can only be transferred to users with Editor, Admin or Superadmin roles',
        errorMessage: 'CARTO could not delete the user. Please try again, or contact us if the problem persists.',
        delete: 'Yes, delete',
        successMessage: 'User deleted successfully'
      },
      makeViewerDialog: {
        title: 'Assign viewer role',
        content: 'Are you sure you want to assign {email} a viewer role?',
        info: 'All maps, workflows, projects, connections and credentials from {userName} must be transferred to another user. M2M OAuth Clients owned by this user will be deleted for security reasons, and any projects using them will stop working. We recommend replacing them before deleting or downgrading this user',
        cancel: 'Cancel',
        confirm: 'Yes, assign viewer role',
        errorMessage: 'CARTO could not change the user role. Please try again, or contact us if the problem persists.',
        successMessage: 'Role changed successfully',
        newAssetOwnerLabel: 'New asset owner:',
        newAssetOwnerPlaceholder: 'Select user',
        newAssetOwnerInfo: 'Assets can only be transferred to users with Editor, Admin or Superadmin roles'
      },
      searchPlaceholder: 'Search users',
      count: '{count, plural, one {# user} other {# users}}',
      filteredCount: '{count} of {total, plural, one {# user} other {# users}}',
      notFound: 'User not found',
      defaultUserRole: {
        title: 'Default role for new users',
        titleWithSsoGroups: 'Default role for new users without groups',
        propertyCaption: 'Current default role',
        dialogMessage: 'Define which role will be assigned by default to new users joining your organization.',
        dialogMessageWithSsoGroups:
          "Define which role will be assigned by default to new users joining your organization that don't belong to any group.",
        success: 'Default user role successfully changed.'
      },
      ssoGroups: {
        definedRole: 'Role defined by its SSO group'
      }
    },
    groups: {
      title: 'Groups',
      searchPlaceholder: 'Search groups',
      notFound: 'No groups matching your search criteria',
      empty: 'No groups have been synchronized yet',
      info: '<strong>Your groups are automatically synchronized</strong> using your Company Single Sign-On, also known as SSO. Every time an user in your organization logs in, we retrieve the group info from your company directory. <link>Read more in the documentation</link>.',
      actions: {
        rename: 'Rename group alias',
        showMembers: 'Show members',
        delete: 'Delete'
      },
      createDialog: {
        createGroup: 'Create group',
        title: 'Create a new group',
        description: 'Pre-create groups in CARTO before they are synchronized from your Single Sign-On (SSO){details}.',
        roleManagement: ', so that new users coming from that group get its assigned role',
        fields: {
          customName: 'Group alias',
          externalName: 'SSO group ID',
          externalNameInfo: 'This must be the ID of the desired group in your Identity Provider (IdP)',
          roleMapping: 'Group role mapping'
        },
        errors: {
          customName: 'This group alias is already being used, please write a new one.',
          externalName: 'This SSO group ID is already being used, please write a new one.'
        }
      },
      deleteDialog: {
        title: 'Delete group',
        content1:
          "You're about to delete the {groupName} group. Any assets shared with this group will no longer be accessible to its members.",
        content2:
          'If this group is not removed from your Identity Provider (IdP), it will be recreated the next time a group member logs in. <a>Learn more</a>',
        confirm: 'Yes, delete it',
        successNotificationMessage: 'Group successfully deleted.',
        errorNotificationMessage: 'Error deleting the group'
      },
      selectAnAdminGroupDialog: {
        title: 'Select an admin group',
        description:
          'Before mapping your groups to roles, you need to select one admin group (you can map more in the future) <strong>to avoid losing admin access to the organization</strong>. Our recommendation is that you choose a group that you, as an admin, belong to.',
        info: 'After selecting an admin group, you can continue the group-role mapping in the "Groups" tab.',
        group: 'Group',
        selectGroup: 'Select a group'
      },
      roleChangeDialog: {
        title: 'Group role mapping',
        confirm: 'Yes, assign {role} role',
        content: 'Are you sure you want to assign the {role} role to the group {groupName}?',
        info: 'The role for each user will be updated the next time they log in.',
        infoViewer:
          'All assets (such as maps, connections and apps) owned by the users in this group will be transferred to you.',
        defaultRoleLabel: 'Select group role'
      },
      renameDialog: {
        rename: 'Rename',
        genericErrorMessage: 'Something went wrong',
        duplicatedErrorMessage: 'This group alias is already in used'
      },
      table: {
        headCell: {
          alias: 'Group alias',
          name: 'SSO group name',
          ssoId: 'SSO group ID',
          members: 'Members',
          groupRole: 'Group role'
        }
      }
    },
    groupMembers: {
      dialog: {
        title: '{group_name} members'
      },
      searchPlaceholder: 'Search group members',
      notFound: 'No users where found',
      tryAgain: 'Try another search',
      empty: 'No groups members have been synchronized yet',
      table: {
        headCell: {
          email: 'User',
          role: 'Role'
        }
      }
    },
    apps: {
      title: 'Applications',
      description:
        'As an Admin, you can add any application (developed with or without CARTO) to the Workspace, so that all or some of your users have an easy access to it. These applications will be shown in the Applications page.',
      table: {
        headCell: {
          name: 'Name',
          added: 'Added'
        }
      },
      registerNew: 'Add new',
      registerNewShort: 'New',
      notFound: 'No registered apps found',
      createdApp: {
        savedSuccess: 'Changes saved successfully'
      },
      editedApp: {
        savedSuccess: 'App edited successfully'
      },
      registerAppDialog: {
        title: 'Registered app',
        edit: 'Edit registered app',
        create: 'Create',
        save: 'Save',
        savedSuccess: 'Registered app saved successfully'
      }
    },
    branding: {
      info: {
        lookingEnable: {
          title: 'Looking to customize CARTO?',
          message: "Let us know and we'll help you get started with your custom appearance."
        },
        upgrade: {
          title: 'Upgrade your plan',
          message:
            "Customizing the appearance in CARTO requires a larger Enterprise plan. Let us know if you're interested and we'll guide you through the process."
        }
      }
    },
    integrations: {
      title: 'Integrations'
    },
    analytics: {
      analyticsToolbox: 'Analytics Toolbox',
      title: 'Installations',
      newInstallation: 'New installation',
      none: "You don't have an Analytics Toolbox installation yet",
      help: 'Learn how to install the CARTO Analytics Toolbox',
      table: {
        header: {
          dataWarehouse: 'Data warehouse',
          location: 'Location',
          installationDate: 'Installation Date',
          version: 'Version',
          status: 'Status'
        },
        actions: {
          install: 'Install',
          changeCredentials: 'Change credentials',
          uninstall: 'Uninstall'
        }
      },
      selectDataWarehouse: {
        subTitle: 'Select a data warehouse',
        helpDefault: 'Select a data warehouse to install the Analytics Toolbox',
        snowflake: {
          p1: 'The {provider} Analytics Toolbox provides geospatial functions to perform fundamental spatial operations, such as clustering and data enrichment, natively within the {provider} environment.',
          p2: '<a>See documentation</a>.'
        },
        manual: {
          p1: 'Currently we only have support for a <strong>manual installation process</strong>.',
          p1BigQuery:
            'Access to the Analytics Toolbox via your connections to BigQuery is automatic except for VPC environments, for which we only have support for a <strong>manual installation process</strong>.',
          p2: 'In order to receive assistance and all necessary instructions to complete the manual installation please contact CARTO at <a>support@carto.com</a>.',
          p3: 'For more details about the Analytics Toolbox for {provider}, please access <a>our product documentation</a>.'
        }
      },
      connectWarehouseForm: {
        title: 'New installation',
        validateSetup: 'Validate setup',
        common: {
          requiredSetup: 'Required setup'
        },
        snowflake: {
          info: {
            p1: 'CARTO requires a specific setup in your Snowflake account to be able to securely install the Analytics Toolbox and then access it from our different  platform interfaces. To ensure the best experience we request that you create a database and a schema called  ‘carto’ , plus a user and a role, to connect in a secure way to perform the installation and future updates.',
            p2: 'To ensure the best experience, please follow the <a>detailed instructions</a> to securely setup your Snowflake account for the Analytics Toolbox installation.'
          },
          form: {
            requiredSetupConfirmation: 'I have already completed the setup of my Snowflake account',
            title: 'Connect to your Snowflake account',
            info: 'Provide the following details in order to establish a connection with the ‘carto’ user to the ‘carto’ database and ‘carto’ schema that you have recently setup in order to continue with the Analytics Toolbox installation. CARTO will also use this connection to update the Analytics Toolbox when new versions become available.',
            username: 'Username',
            database: 'Database',
            password: 'Password',
            role: 'Role',
            account: 'Account Name'
          }
        }
      },
      atInstallationDialog: {
        validation: {
          title: 'Setup validation',
          info: 'Validating that we have access to all the necessary resources before proceeding with the installation.',
          inProgressStatus: 'Access to Snowflake account…'
        },
        installation: {
          title: 'Installation',
          info: ' ',
          inProgressStatus: 'Installing Analytics Toolbox'
        },
        completed: 'Completed',
        install: 'Install'
      },
      atUninstallationDialog: {
        title: 'Uninstall',
        confirmQuestion: 'Do you want to uninstall this Analytics Toolbox?',
        uninstall: 'Yes, uninstall',
        inProgressStatus: 'Uninstalling Analytics Toolbox',
        completed: 'Completed',
        error: 'Error',
        failedAlert: 'There was a problem during the uninstallation process, please try again.'
      },
      atUpdateDialog: {
        title: 'Analytics Toolbox Update',
        message: {
          p1: 'Version {version} is available for {providerLabel}. Would you like to install it now??',
          p2: 'Check the <a>release notes</a> for more details'
        },
        update: 'Yes, update',
        inProgressStatus: 'Updating Analytics Toolbox',
        completed: 'Completed',
        error: 'Error',
        failedAlert: 'There was a problem during the installation process, please try again.'
      },
      changeConfigForm: {
        title: 'Change credentials',
        validateAndSave: 'Validate and save',
        prompt: 'Your Snowflake credentials',
        successNotification: 'Credentials has been successfully changed.'
      },
      atUpdateConfigDialog: {
        title: 'Credentials validation',
        prompt: 'Validating that we have access to all the necessary resources.',
        updateConfig: 'Yes, update config',
        inProgressTitle: 'Validation',
        inProgressStatus: 'Access to snowflake account',
        completed: 'Completed',
        error: 'Error',
        failedAlert: 'There was a problem saving new config process, please try again.'
      },
      states: {
        available: 'Available',
        updateAvailable: 'Update available'
      }
    },
    assetManagement: {
      default: {
        title: 'Untitled'
      },
      maps: {
        title: 'Maps',
        searchPlaceholder: 'Search map',
        noResults: 'No maps were found',
        emptyState: "Your organization doesn't have any maps yet.",
        tableHead: {
          name: 'Name',
          owner: 'Owner',
          lastUpdated: 'Last updated',
          sharedWith: 'Shared with'
        }
      },
      workflows: {
        title: 'Workflows',
        searchPlaceholder: 'Search workflow',
        noResults: 'No workflows were found',
        emptyState: "Your organization doesn't have any workflows yet.",
        exposedAs: {
          all: 'All workflows',
          allAlternative: 'Exposed as',
          mcp: 'MCP',
          api: 'API'
        }
      },
      connections: {
        title: 'Connections',
        searchPlaceholder: 'Search connection',
        noResults: 'No connections were found',
        emptyState: "Your organization doesn't have any connections yet."
      },
      accessTokens: {
        searchPlaceholder: 'Search API Access Token',
        noResults: 'No API Access Tokens were found',
        emptyState: "Your organization doesn't have any API Access Token yet."
      },
      m2mOAuthClients: {
        searchPlaceholder: 'Search M2M OAuth Client',
        noResults: 'No M2M OAuth Clients were found',
        emptyState: "Your organization doesn't have any M2M OAuth Client yet."
      },
      spaOAuthClients: {
        searchPlaceholder: 'Search SPA OAuth Client',
        noResults: 'No SPA OAuth Clients were found',
        emptyState: "Your organization doesn't have any SPA OAuth Client yet."
      },
      developers: {
        title: 'Developers',
        selector: 'Credential type'
      },
      tableHead: {
        name: 'Name',
        owner: 'Owner',
        lastUpdated: 'Last updated',
        sharedWith: 'Shared with',
        maps: 'Maps',
        exposedAs: 'Exposed as',
        token: 'Token',
        clientId: 'Client ID'
      },
      actions: {
        delete: 'Delete',
        transfer: 'Transfer',
        assetsSelected: '{count} assets selected',
        transferDialog: {
          header: 'Transfer {assetTypes}',
          contentOne: 'The {assetTypes} {assetName} will be transferred',
          contentGroup: '{count} {assetTypes}s will be transferred',
          autocompleteFromLabel: 'From:',
          autocompleteToLabel: 'To:',
          autocompleteToPlaceholder: 'Select user',
          infoCaption: 'Assets can only be transferred to users with Editor, Admin or Superadmin roles',
          infoCaptionCredentials:
            'Credentials can only be transferred to users with Editor, Admin or Superadmin roles.',
          warning:
            'Transferred assets may have errors if the new owner does not have access to the connections/sources used.',
          warningTokens:
            "Transferred tokens will use the new owner's level of access to the connections defined in the token grants. Review the new setup to avoid disruption in your existing apps using this token",
          warningCredentials:
            'Transferred credentials may have errors if the new owner does not have access to the same resources.',
          warningConnection:
            "Transferring a connection may lead to errors in maps, workflows, or apps that use the transferred connection. <br></br><br></br>By sharing this connection you're also sharing the permissions it might have.",
          transferButton: 'Transfer',
          successTransferMessage: '{resourceType} transferred successfully',
          partialErrorMessage:
            'CARTO could not transfer {failed} of {totalCount} selected assets. Please try again, or contact us if the problem persists.',
          errorMessage:
            'CARTO could not transfer the selected asset. Please try again, or contact us if the problem persists.'
        },
        deleteDialog: {
          header: 'Delete {assetTypes}',
          contentOne: 'Are you sure you want to delete {assetName} {credentialType}? This action cannot be undone.',
          contentGroup: 'Are you sure you want to delete {count} selected {assetTypes}s? This action cannot be undone.',
          cancelButton: 'Cancel',
          confirmDeleteButton: 'Yes, delete',
          warningCredential: 'Existing applications using this credential will be affected',
          warningConnectionTitle: 'Deleting these connections will also delete:',
          warningConnectionBody: '{mapCount} maps and {workflowCount} workflows',
          successDeleteMessage: '{resourceType} deleted successfully',
          partialErrorMessage:
            'CARTO could not delete {failed} of {totalCount} selected assets. Please try again, or contact us if the problem persists.',
          errorMessage:
            'CARTO could not delete the selected asset. Please try again, or contact us if the problem persists.'
        }
      }
    },
    dataObservatory: {
      subscriptions: 'Subscriptions',
      table: {
        name: 'Name',
        version: 'Version',
        license: 'License',
        expirationDate: 'Expiration',
        availableIn: 'Available in',
        status: 'Status'
      },
      statusLabels: {
        subscribed: 'Subscribed',
        expired: 'Expired',
        expiresSoon: 'Expires soon'
      },
      noExpirationDate: 'None',
      emptyState: {
        title: 'You currently do not have any data subscription in your organization',
        subtitle: 'Explore the catalog of spatial data  in our Data Observatory',
        button: 'Go to Data Observatory'
      },
      removeSubscriptionDialog: {
        title: 'Remove subscription from the list',
        content:
          'Are you sure you want to remove this item from the list? This subscription will no longer be visible in this page.',
        cancelButton: 'Cancel',
        confirmButton: 'Yes, remove',
        subscriptionRemovedSuccessfully: 'Subscription successfully removed'
      },
      removePublicSubscriptionDialog: {
        title: 'Unsubscribe',
        content: 'Are you sure you want to unsubscribe from this dataset?',
        footerContent:
          'This action will remove this subscription for all users of your organization. Maps and workflows that make use of this data subscription may be affected.',
        cancelButton: 'Cancel',
        confirmButton: 'Yes, unsubscribe',
        subscriptionUnsubscribedSuccessfully: "You've unsubscribed successfully"
      },
      actions: {
        unsubscribe: 'Unsubscribe',
        removeSubscription: 'Remove from list',
        requestTransfer: 'Request transfer',
        copy: 'Copy table name',
        copied: 'Copied!'
      },
      requestTransferDialog: {
        title: 'Request transfer',
        description: 'Request to transfer this subscription to your data warehouse.',
        transferTo: 'Transfer to',
        successNotification: 'Your request has been sent successfully',
        errorNotification: 'There was an error sending your request',
        placeholder: 'Select a data warehouse provider',
        regionPlaceholder: 'Type to search your region',
        regionNotListed: 'Other',
        regionOther: 'Which region do you need?',
        regionOtherHelp: 'We will get in touch to confirm whether this region is available.',
        manualSetup:
          "Automatic transfers to this destination are not yet supported. Submit a request, and we'll get in touch with you.",
        cartoDw: {
          region: 'Region'
        },
        bigquery: {
          region: 'BigQuery Region',
          group: 'Google Account / Google Group / Service Account',
          groupPlaceholder: 'e.g.: data-team@mycompany.com'
        },
        snowflake: {
          accountName: 'Snowflake Account Name',
          accountNamePlaceholder: 'e.g.: MYACCOUNT',
          orgName: 'Snowflake Organization Name',
          orgNamePlaceholder: 'e.g.: MYORGANIZATION',
          cloud: 'Cloud',
          region: 'Region'
        },
        databricks: {
          cloud: 'Cloud',
          region: 'Region',
          metastoreId: 'Databricks Metastore ID',
          metastoreIdPlaceholder: 'e.g.: d7c5b8f2-41a3-4b9c-9f18-7e84c3e9a1b2'
        }
      }
    }
  },
  supportAccess: {
    title: 'Enabling Support access',
    description:
      'Allow CARTO temporary access to your organization so that our Support and Customer Success teams can onboard you, assist with new functionality, or troubleshoot issues. You can revoke this access at any time.',
    enableButton: 'Enable access',
    disabledMessage:
      'Support access is disabled because your organization is enforcing Single Sign-On. <a>More info</a>',
    accessAllowed: 'Access allowed',
    accessAllowedDescription: 'Support access granted on {date} by {user}. Due to expire on {expirationDate}.',
    revokeButton: 'Revoke access',
    revokeDialog: {
      title: 'Revoke Support access',
      description:
        'Our team will no longer have access to your organization. You can grant us permission again at any time.',
      alert:
        'All maps, workflows, projects, connections and credentials from {user} must be transferred to another user',
      form: {
        assetOwner: 'New asset owner:',
        assetOwnerDescription: 'Assets can only be transferred to users with Editor, Admin or Superadmin roles',
        button: 'Revoke access'
      },
      success: 'Support access revoked.'
    },
    enableDialog: {
      title: 'Enable Support access',
      description:
        'When enabling Support access, you authorize specific support personnel within CARTO to have administrator-level role access to your Workspace. You can remove this access at any time.',
      form: {
        duration: 'Duration',
        durationDays: '{count, plural, one {# day} other {# days}}',
        authorize: 'Authorize access',
        authorizeInfo:
          'I understand that by enabling access, I authorize the CARTO team to access my organization for the specified time period.',
        button: 'Enable access'
      },
      success: 'Support access enabled.'
    },
    userEmail: 'CARTO Support',
    userActionsInfo: 'You can manage CARTO Support access from the General section of the organization settings'
  },
  delete: {
    title: 'Delete organization',
    warning:
      'Delete the organization, including all the maps, connections, users (including yourself) and any content created. Proceed with caution.',
    button: 'Delete organization',
    dialog: {
      title: 'Delete organization',
      description: {
        line1: "Are you sure you want to delete the whole organization? This action can't be undone",
        line2: 'Enter the name of the organization to confirm the action'
      },
      accountNameInput: {
        label: 'Organization name'
      },
      deleteButton: 'Yes, delete organization'
    }
  },
  lds: {
    quotas: {
      dialog: {
        cancel: 'Cancel'
      },
      geocoding: {
        title: "Couldn't process geocoding",
        subtitle:
          "You're running out of location data services quota. Check if the table you requested is too large for the remaining quota available.",
        barLabel: 'Geocoding and Isolines',
        warning: 'Remember that your quota will be reset on {date}. Request to be contacted for an upgrade.'
      }
    }
  },
  roles: {
    admin: 'Admin',
    editor: 'Editor',
    builder: 'Editor',
    viewer: 'Viewer',
    superadmin: 'Superadmin',
    guest: 'Guest Viewer'
  },
  branding: {
    title: 'Appearance',
    description:
      'Use your own colors and logo to whitelabel the CARTO interface and provide a custom branded experience for all users.',
    customizeMobile: 'Customize',
    customize: 'Customize appearance',
    logo: 'Logo',
    changeLogo: 'Change logo',
    logoHelper: 'Logo must be square, min size 256x256 and max size 1MB',
    colors: 'Colors',
    primaryColor: 'Primary color',
    primaryColorTooltip:
      'Used to represent primary interface elements such as buttons, menu backgrounds, checkbox colors...',
    secondaryColor: 'Secondary color',
    secondaryColorTooltip: 'Used for accent in some contexts like charts or data workflow',
    appBarBackgroundColor: 'App Bar background color',
    appBarBackgroundColorTooltip: 'Used for accent in some contexts like charts or data workflows',
    appBarContrastTextColor: 'App Bar contrast text',
    appBarContrastTextColorTooltip: 'The text color for the items and title in the top bar',
    usePrimaryColorsForAppBarLabel: 'Use primary colors for App Bar (header)',
    updateError: 'An error occurred while branding update, please try again.',
    logoUpdateError: 'An error occurred while logo upload, please try again.',
    publicMaps: {
      title: 'Public and embedded maps',
      description:
        'Customize your public and embedded maps by showing or removing the CARTO footer (including the logo and social media icons)',
      control: 'Show CARTO footer'
    },
    onboarding: {
      title: 'Onboarding',
      description:
        'Customize whether new users in this organization will see the regular onboarding experience when they first join CARTO',
      control: 'Show onboarding to new users'
    },
    getInTouch: 'Get in touch with the CARTO team to get access to this functionality',
    customCodeInBuilder: {
      title: 'Custom code for maps',
      description:
        'Add custom code to all your organization’s Builder maps. This code will be applied whether accessing a map shared with the organization/groups or a publicly available map.',
      warning: {
        title: "CARTO doesn't validate custom code",
        description:
          "Custom code is an advanced modification that may conflict with CARTO's functionality. Additionally, our support team is unable to provide direct assistance with custom code setup or troubleshooting."
      },
      header: {
        title: 'Custom HTML',
        saveHtml: 'Save HTML',
        successMessage: 'HTML code saved successfully'
      }
    }
  },
  basemaps: {
    title: 'Custom basemaps',
    empty: "You haven't added any basemap yet",
    new: 'New basemap',
    limitReached: 'You have reached the limit of {limit} custom basemaps.',
    switches: {
      title: 'Enabled basemaps in Builder',
      carto: 'CARTO basemaps',
      google: 'Google Maps basemaps',
      custom: 'Custom basemaps'
    },
    tooltips: {
      atLeastOneCategory: 'At least one basemap category must remain active',
      atLeastOneCustomBasemap: 'At least one basemap must exist if custom category is the only one enabled',
      addBeforeEnable: 'Add a custom basemap to enable this option',
      noGoogleApiKey: 'There is no Google Maps API key set for the tenant'
    },
    deleteDialog: {
      title: 'Delete custom basemap',
      success: 'Basemap deleted',
      description: {
        1: 'Are you sure you want to delete this basemap? This action cannot be undone.',
        2: '<strong>basemapName</strong> basemap will no longer be accessible to Editors in CARTO Builder.',
        3: "Existing maps using this basemap will remain unaffected, but Editors won't be able to use it when creating or editing maps."
      }
    },
    dialog: {
      title: 'New custom basemap',
      previewNotAvailable: 'Preview not available',
      form: {
        url: 'URL',
        urlHelper: 'Find out more about the supported URL formats in our <a>documentation</a>',
        urlError: 'Invalid URL. Check and submit a valid URL for basemap addition.',
        name: 'Name',
        urlPlaceholder: 'Paste or type the URL',
        namePlaceholder: 'Type the name',
        attribution: 'Attribution',
        attributionPlaceholder: 'Type the copyright attribution'
      },
      add: 'Add basemap'
    },
    detailsDialog: {
      delete: 'Delete basemap'
    }
  },
  workflows: {
    extensionPackages: {
      title: 'Extension Packages',
      description:
        'Extend Workflows functionality with the installation of Extension Packages including custom components for more specialized analytics.',
      enabled: 'Allow the installation of Extension Packages in Workflows'
    }
  },
  organizationMarkers: {
    section: {
      title: 'Organization markers library',
      emptyTitle: "You haven't added any Organization custom markers yet.",
      import: 'Import markers',
      docs: 'View documentation'
    },
    import: {
      title: 'Import markers',
      description: 'Import local SVG or PNG files from your computer.',
      files: {
        label: 'Files',
        placeholder: 'E.g.: icon.svg, image.png',
        browse: 'Browse'
      },
      dropzone: {
        aria: 'Drop SVG or PNG markers here',
        title: 'Drag and drop files to import',
        hint: 'Only SVG and PNG files are supported. Max. 2MB each file.',
        batchTooLarge: 'You can import up to {limit} markers at a time. Please split the batch.'
      },
      selected: '{count, plural, one {# file selected} other {# files selected}}',
      remove: 'Remove {name} from import',
      continue: 'Continue',
      back: 'Back',
      submit: 'Import {count, plural, one {# marker} other {# markers}}',
      accept: 'Accept',
      leaveConfirm: {
        description: 'If you close the dialog, no markers will be imported.'
      },
      edit: {
        counter: '{count, plural, one {# marker to import.} other {# markers to import.}} Review them if necessary.',
        tableAria: 'Markers to import',
        col: {
          icon: 'Icon',
          name: 'Name',
          fileType: 'File type'
        },
        nameAria: 'Name for marker at row {index}',
        rowMenu: 'Open actions menu for {name}',
        remove: 'Remove',
        conflictAlert: {
          title:
            '{count, plural, one {# marker matches an existing marker in your library.} other {# markers match existing markers in your library.}}',
          description: 'If you import them, they will replace the ones in your library.'
        },
        duplicateAlert: {
          title: '{count, plural, one {# duplicated name in this batch.} other {# duplicated names in this batch.}}',
          description: 'Rename the highlighted rows so each marker has a unique name.'
        }
      },
      notification: {
        allSuccess: '{count, plural, one {Marker imported successfully} other {# markers imported successfully}}',
        partial: '{succeeded} imported, {failed} failed.',
        allFailed: 'Could not import {count, plural, one {# marker} other {# markers}}.',
        removeFromBatch: 'Marker removed successfully'
      }
    },
    table: {
      aria: 'Organization markers',
      searchPlaceholder: 'Search marker',
      rowMenu: 'Open actions menu for {name}',
      col: {
        icon: 'Icon',
        name: 'Name',
        fileType: 'File type',
        lastUpdated: 'Last updated'
      },
      fileTypeFilter: {
        label: 'File type',
        all: 'All types'
      },
      sort: {
        lastUpdatedDesc: 'Last updated · Newest',
        lastUpdatedAsc: 'Last updated · Oldest',
        nameAsc: 'Name · A-Z',
        nameDesc: 'Name · Z-A'
      },
      noResults: {
        title: 'No markers match your filters.',
        description: 'Try a different search term or file type.'
      },
      rowAction: {
        rename: 'Rename',
        replace: 'Replace marker icon',
        delete: 'Delete'
      },
      selectAllAria: 'Select all markers on this page',
      selectRowAria: 'Select {name}',
      bulk: {
        selected: '{count, plural, one {# marker selected} other {# markers selected}}',
        // Visible label matches the Figma copy (plain "Delete"); the
        // `deleteAria` value below disambiguates the toolbar action from the
        // row-menu Delete for screen reader users without changing the visual.
        delete: 'Delete',
        deleteAria: 'Delete selected'
      }
    },
    rename: {
      title: 'Rename marker',
      description: 'Change or edit the marker name.',
      label: 'Marker name',
      save: 'Save',
      conflictError: 'A marker with this name already exists.',
      notification: {
        success: 'Changes saved successfully',
        error: 'Could not rename the marker.'
      }
    },
    replace: {
      title: 'Replace marker icon',
      description: 'Import local SVG or PNG files from your computer to replace the marker icon.',
      fileLabel: 'File',
      browse: 'Browse',
      helper: 'Only SVG and PNG files are supported. Max. size {maxMb}MB.',
      sizeError: 'File is larger than {maxMb} MB.',
      typeError: 'Only SVG and PNG files are supported.',
      previewLabel: 'Marker icon',
      previewAlt: 'Marker icon preview for {name}',
      submit: 'Import',
      notification: {
        success: 'Marker imported successfully',
        error: 'Could not replace the marker icon.'
      }
    },
    delete: {
      title: 'Delete marker',
      confirmation: 'Are you sure you want to delete {name}? This action cannot be undone.',
      fallbackNote:
        'Any layers currently using this marker will revert to the default point styling. Editors will no longer be able to apply it when creating or editing maps.',
      confirm: 'Yes, delete',
      notification: {
        success: 'Marker deleted successfully',
        notFound: 'This marker was no longer available.',
        error: 'Could not delete the marker.'
      }
    },
    deleteBatch: {
      title: 'Delete markers',
      confirmation:
        'Are you sure you want to delete {count, plural, one {# selected marker} other {# selected markers}}? This action cannot be undone.',
      fallbackNote:
        "These markers will no longer be accessible to Editors in CARTO Builder. Existing maps using them will remain unaffected, but Editors won't be able to use them when creating or editing maps.",
      confirm: 'Yes, delete',
      notification: {
        success: '{count, plural, one {Marker deleted successfully} other {# markers deleted successfully}}',
        error: 'Could not delete the selected markers.'
      }
    }
  },
  palettes: {
    title: 'Organization color palettes',
    subtitle:
      'These custom color palettes will be available to all editors in your organization when styling layers in CARTO Builder maps.',
    new: 'New palette',
    limitReached: 'You have reached the limit of {limit} organization color palettes.',
    actions: {
      edit: 'Edit',
      duplicatePalette: 'Duplicate palette',
      delete: 'Delete'
    },
    deleteSuccess: 'Palette deleted',
    newPaletteDialog: {
      title: 'New organization palette',
      name: 'Name',
      helperTextAlreadyExists: 'There is already a palette with this name.',
      namePlaceholder: 'Name of the new palette',
      type: 'Type',
      typePlaceholder: 'Select a palette type',
      typeOptions: {
        diverging: 'Diverging',
        sequential: 'Sequential',
        qualitative: 'Qualitative',
        singlehue: 'Singlehue',
        custom: 'Custom'
      },
      addColor: 'Add color',
      createPalette: 'Create palette',
      deletePalette: 'Delete palette',
      confirmDelete: 'Yes, delete',
      done: 'Done',
      keepEditing: 'Keep editing',
      warningConfirmationContent: 'If you leave, you will lose your current colour palette.',
      warningConfirmDeleteTitle: 'Delete organization palette',
      warningConfirmDeleteContent1: 'Are you sure you want to delete this palette? This action cannot be undone.',
      warningConfirmDeleteContent2: '{palette_name} palette will no longer be accessible to Editors in CARTO Builder.',
      warningConfirmDeleteContent3:
        "Existing maps using this palette will remain unaffected, but Editors won't be able to use it when creating or editing maps."
    },
    deletePaletteDialog: {
      title: 'Delete organization palette',
      description1: 'Are you sure you want to delete this palette? This action cannot be undone.',
      description2: '{palette_name} palette will no longer be accessible to Editors in CARTO Builder.',
      description3:
        "Existing maps using this palette will remain unaffected, but Editors won't be able to use it when creating or editing maps.",
      cancel: 'Cancel',
      confirm: 'Yes, delete'
    }
  },
  // Shared by every usage-by-user breakdown (AI usage rankings, quota limits,
  // usage analytics) so each one renders the consolidated entry identically.
  usageAttribution: {
    publicApiTokens: {
      label: 'Public/API Access Tokens',
      tooltip: 'Usage coming from public maps or API Access Tokens where the actual user is not identifiable.'
    }
  },
  ai: {
    title: 'CARTO AI',
    subtitle: 'Enable CARTO AI',
    description:
      'Enable access to AI capabilities across the CARTO platform. This includes AI Agents in Builder for natural language interaction with maps, along with other AI features where supported. Learn more about CARTO AI <a>in our Documentation</a>',
    documentationLink: 'Learn more about CARTO AI in our Documentation',
    enableToggle: 'Enable CARTO AI',
    unavailable: {
      cartoAiOff: {
        admin: 'Enable CARTO AI in Settings in order to use this feature',
        member: 'Ask your Admin to enable CARTO AI in order to use this feature'
      },
      featureOff: {
        admin: 'Enable this AI feature in Settings in order to use it',
        member: 'Ask your Admin to enable this AI feature in Settings.'
      }
    },
    mcpServer: {
      title: 'CARTO MCP Server',
      section: 'CARTO AI',
      description:
        "This is CARTO's MCP Server. Add this URL to any compatible MCP client to make CARTO's tools available to your AI agents and assistants, so they can explore your data, create and preview maps, run Workflows, and more. See <a>our documentation</a> to learn how to authenticate and get started.",
      urlLabel: 'MCP Server URL'
    },
    tabs: {
      configuration: 'Configuration',
      analytics: 'Analytics',
      analyticsDisabledTooltip: 'Enable CARTO AI to view analytics.'
    },
    analytics: {
      freshness: 'Data refreshed daily. Figures are scoped to your organization.',
      aiCapabilitiesNote: 'CARTO AI capabilities comprise CARTO AI Agents, AI API, CARTO CLI, and MCP Server.',
      subtabs: {
        allActivity: 'All activity',
        cartoAgents: 'CARTO Agents',
        externalAgents: 'External Agents',
        ariaLabel: 'Analytics dimension'
      },
      sections: {
        activeUsers: 'Active users',
        usageQuota: 'Usage quota',
        aiQuota: 'AI usage',
        topUsersByUsageQuota: 'Top 10 users by Usage quota',
        topUsersByAiQuota: 'Top 10 users by AI usage',
        cartoAgents: 'CARTO Agents',
        conversations: 'Conversations',
        mcpTools: 'MCP Tools',
        mapsCreated: 'Maps created',
        workflowsCreated: 'Workflows created'
      },
      scorecards: {
        wau: 'Weekly active users',
        mau: 'Monthly active users',
        usageQuota: 'Total usage quota consumed',
        aiQuota: 'Total',
        numAgents: 'Total CARTO AI Agents',
        numConversations: 'Total Conversations',
        numMcpExecutions: 'Total MCP Tool executions',
        mapsCreated: 'Total maps created',
        workflowsCreated: 'Total workflows created',
        mcpExecutionsExternal: 'Total MCP Tool executions'
      },
      descriptions: {
        wau: 'Distinct authenticated users in your organization that engaged with CARTO AI capabilities during the last full week.',
        mau: 'Distinct authenticated users in your organization that engaged with CARTO AI capabilities during the last 30 days.',
        usageQuota:
          'Usage quota consumption attributed to Agentic GIS, including requests originating from CARTO AI Agents, the CARTO CLI and the MCP Server. Check {docsLink} for more details.',
        aiQuota:
          'AI tokens consumed across all AI providers, including CARTO-managed models and custom models configured by your organization.',
        numAgents: 'Current number of CARTO AI Agents configured.',
        numConversations: 'Number of conversations with CARTO Agents.',
        numMcpExecutionsCarto: 'Workflows as MCP Tools called by CARTO Agents.',
        mapsCreated: "Maps created by using CARTO's CLI.",
        workflowsCreated: "Workflows created by using CARTO's CLI.",
        mcpExecutionsExternal: "MCP Tools executed from CARTO's MCP Server."
      },
      docsLinkLabel: 'our documentation',
      timeSeries: {
        dau: 'Daily Active Users',
        wau: 'Weekly Active Users',
        mau: 'Monthly Active Users',
        usageQuota: 'usage quota consumed',
        aiQuota: 'AI tokens consumed',
        numAgents: 'CARTO AI Agents',
        numAgentsCreated: 'CARTO AI Agents created',
        numConversations: 'conversations',
        mcpExecutions: 'MCP Tool executions',
        mapsCreated: 'maps created',
        workflowsCreated: 'workflows created'
      },
      dimension: {
        all: 'All agents',
        carto: 'CARTO Agents',
        external: 'External Agents'
      },
      ranges: {
        '1M': '1 Month',
        '3M': '3 Months',
        '1Y': '1 Year'
      },
      periodLabel: {
        '1M': 'in the last month',
        '3M': 'in the last 3 months',
        '1Y': 'in the last year',
        contractCycle: 'in the current contract cycle'
      },
      weeklyTotalsHint: 'Shown as weekly totals.',
      trend: {
        twoWeeksAgo: '2 weeks ago',
        fourWeeksAgo: '4 weeks ago'
      },
      units: {
        unitsValue: '{value} units',
        tokensValue: '{value} tokens',
        percentOfQuota: '{pct}% of quota'
      },
      topTen: {
        usersTable: {
          userId: 'User',
          usageQuota: 'Usage quota',
          aiQuota: 'AI usage',
          deletedUser: 'Deleted user'
        },
        deletedMap: 'Deleted map',
        usersByAgents: 'Top 10 users by number of CARTO AI Agents created',
        usersByConversations: 'Top 10 users by number of conversations',
        agentsByConversations: 'Top 10 AI Agents by number of conversations',
        toolsByExecutions: 'Top 10 MCP Tools by number of executions',
        agentsByToolExecutions: 'Top 10 AI Agents by number of MCP Tools executions',
        usersByMaps: 'Top 10 users by maps created',
        usersByWorkflows: 'Top 10 users by workflows created',
        usersByMcp: 'Top 10 users by number of MCP Tool executions'
      },
      empty: {
        activeUsersTitle: 'No active users yet',
        activeUsersDescription:
          'Once anyone in your organization starts using CARTO AI capabilities, active user counts will appear here.',
        usageQuotaTitle: 'No agentic Usage quota consumed yet',
        usageQuotaDescription:
          'As soon as CARTO AI capabilities generate API requests, their share of your Usage quota will show up here.',
        aiQuotaTitle: 'No AI usage yet',
        aiQuotaDescription:
          'Once your organization starts consuming AI tokens through any provider, consumption and trends will appear here.',
        topUsersByUsageQuotaTitle: 'No Usage quota consumption to rank yet',
        topUsersByUsageQuotaDescription:
          'Once users in your organization start consuming Usage quota through AI features, the top consumers will be listed here.',
        topUsersByAiQuotaTitle: 'No AI usage to rank yet',
        topUsersByAiQuotaDescription:
          'Once users in your organization start consuming AI tokens through any provider, the top consumers will be listed here.',
        numAgentsTitle: 'No CARTO AI Agents yet',
        numAgentsDescription:
          'Once a CARTO AI Agent is added to a Builder map in your organization, metrics will appear here.',
        conversationsTitle: 'No conversations yet',
        conversationsDescription:
          'Once users start chatting with your CARTO AI Agents, conversation counts and the most active users and agents will appear here.',
        cartoMcpTitle: 'No MCP Tool executions yet',
        cartoMcpDescription:
          'Once your CARTO AI Agents start calling workflows exposed as MCP Tools, executions will appear here; including the most-used tools and the agents invoking them.',
        mapsCreatedTitle: 'No maps created via the CLI yet',
        mapsCreatedDescription:
          'Once you start using external AI Agents to create maps using the CARTO CLI, activity will appear here; including daily creations and a ranked list of top contributors.',
        workflowsCreatedTitle: 'No workflows created via the CLI yet',
        workflowsCreatedDescription:
          'Once you start using external AI Agents to create workflows using the CARTO CLI, activity will appear here; including daily creations and a ranked list of top contributors.',
        workflowsTopUsersTitle: 'No contributors to rank yet',
        externalMcpTitle: 'No MCP Tool executions yet',
        externalMcpDescription:
          "Once external AI Agents start calling tools through CARTO's MCP Server, executions will appear here; including the most-used tools and the users invoking them.",
        noDataYet: 'No data yet'
      },
      error: {
        title: 'Could not load analytics',
        description: 'Refresh the page to try again. If the problem persists, contact support.'
      },
      timeRangeSelector: {
        ariaLabel: 'Time range'
      },
      granularityToggle: {
        ariaLabel: 'Granularity'
      }
    },
    error: 'Cannot enable AI. Required prerequisites are not configured in your deployment.',
    errorLink: 'See configuration guide.',
    termsText:
      'By enabling CARTO AI, you agree to the {actionLink}, including any future updates thereto. CARTO will notify you of any changes.',
    termsLink: 'CARTO AI terms and conditions',
    defaultModel: {
      label: 'Default model',
      description: 'Model used by default for CARTO AI features. You can change it at any time.'
    },
    features: {
      sectionTitle: 'AI Features and default models',
      sectionDescription: 'Enable AI features for your organization and select a default model for each.',
      aiAgentsInBuilder: {
        name: 'AI Agents in Builder maps'
      },
      askAIInDataObservatory: {
        name: 'AI Assistant in Data Observatory'
      },
      aiAssistantInWorkflows: {
        name: 'AI Assistant in workflows',
        description: 'Use AI to build and run Workflows'
      },
      aiAssistantInAppStudio: {
        name: 'AI Assistant in App Studio'
      },
      aiAssistantInBuilder: {
        name: 'AI Assistant in Builder'
      }
    },
    ownModels: {
      title: 'Connect your own AI provider',
      description:
        'By default, CARTO provides AI capabilities using managed models. If you prefer, you can connect your own AI providers. Once configured, your organization will use your providers instead.'
    },
    modal: {
      success: 'Configuration saved successfully',
      failedModels: {
        notification: 'Configuration saved, but some models could not be validated',
        title: '{count, plural, one {1 model was not added} other {# models were not added}}',
        description:
          '{count, plural, one {This model} other {These models}} could not be validated against the provider, so {count, plural, one {it was} other {they were}} not saved.',
        listAriaLabel: 'Models that failed validation'
      },
      title: 'Setup {model}',
      description: {
        openai: 'Enter your OpenAI API key to connect with GPT models.',
        vertex: 'Enter your Google Cloud details to connect with Vertex AI models.',
        google: 'Enter your Google AI Studio API key to connect with Gemini models.',
        bedrock: 'Enter your AWS credentials to connect with Bedrock models.',
        snowflake: 'Enter your Snowflake account details to connect with Cortex models.',
        anthropic: 'Enter your Anthropic API key to connect with Claude models.',
        databricks: 'Enter your Databricks workspace details to connect with Model Serving endpoints.',
        oracle: 'Enter your Oracle Cloud details to connect with OCI Generative AI models.',
        azure: 'Enter your Azure OpenAI details to connect with your deployed models.',
        custom: 'Enter your OpenAI-compatible API key to connect with your models.'
      },
      openai: {
        apiKey: {
          label: 'API Key',
          placeholder: 'Enter your API Key',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode: 'An API key is already saved. Enter a new one only if you want to replace it.',
          required: 'API Key is required'
        },
        baseUrl: {
          label: 'Base URL',
          placeholder: 'Enter Base URL',
          invalid: 'Invalid URL format. Must be a valid HTTP or HTTPS URL'
        }
      },
      google: {
        apiKey: {
          label: 'API Key',
          placeholder: 'Enter your API Key',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode: 'An API key is already saved. Enter a new one only if you want to replace it.',
          required: 'API Key is required'
        }
      },
      vertex: {
        vertexProject: {
          label: 'Project ID',
          placeholder: 'Enter your GCP Project ID',
          required: 'Project ID is required'
        },
        vertexLocation: {
          label: 'Location',
          placeholder: 'e.g., us-central1',
          required: 'Location is required'
        },
        vertexCredentials: {
          label: 'Service Account Credentials',
          placeholder: 'Paste your service account JSON key',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode:
            'A service account key is already saved. Paste a new JSON key only if you want to replace it.',
          required: 'Service account credentials are required'
        }
      },
      bedrock: {
        awsAccessKeyId: {
          label: 'Access Key ID',
          placeholder: 'Enter your Access Key ID',
          required: 'Access Key ID is required'
        },
        awsSecretAccessKey: {
          label: 'Secret Access Key',
          placeholder: 'Enter your Secret Access Key',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode: 'A secret access key is already saved. Enter a new one only if you want to replace it.',
          required: 'Secret Access Key is required'
        },
        awsRegionName: {
          label: 'Region',
          placeholder: 'e.g., us-east-1',
          required: 'Region is required'
        },
        awsSessionToken: {
          label: 'Session Token',
          placeholder: 'Enter Session Token (if using temporary credentials)',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode: 'A session token is already saved. Enter a new one only if you want to replace it.'
        }
      },
      snowflake: {
        apiKey: {
          label: 'Token',
          placeholder: 'Enter your Snowflake personal token',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode: 'A token is already saved. Enter a new one only if you want to replace it.',
          required: 'Token is required'
        },
        apiBase: {
          label: 'Account URL',
          placeholder: 'Enter your Snowflake Account URL (e.g. xyz123.snowflakecomputing.com)',
          required: 'Account URL is required',
          invalid: 'Invalid URL format. Must be a valid HTTP or HTTPS URL'
        }
      },
      anthropic: {
        apiKey: {
          label: 'API Key',
          placeholder: 'Enter your API Key',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode: 'An API key is already saved. Enter a new one only if you want to replace it.',
          required: 'API Key is required'
        },
        baseUrl: {
          label: 'Base URL',
          placeholder: 'Enter Base URL',
          invalid: 'Invalid URL format. Must be a valid HTTP or HTTPS URL'
        }
      },
      databricks: {
        apiBase: {
          label: 'Host',
          placeholder: 'e.g., https://adb-xxxx.azuredatabricks.net',
          required: 'Host is required',
          invalid: 'Invalid URL format. Must be a valid HTTP or HTTPS URL'
        },
        apiKey: {
          label: 'Token',
          placeholder: 'Enter your Databricks Personal Access Token',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode: 'A token is already saved. Enter a new one only if you want to replace it.',
          required: 'Token is required'
        }
      },
      oracle: {
        ociUser: {
          label: 'User OCID',
          placeholder: 'ocid1.user.oc1..aaaa...',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode: 'A user OCID is already saved. Enter a new one only if you want to replace it.',
          required: 'User OCID is required'
        },
        ociFingerprint: {
          label: 'API Key Fingerprint',
          placeholder: 'aa:bb:cc:dd:ee:ff:00:11:22:33:44:55:66:77:88:99',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode: 'A fingerprint is already saved. Enter a new one only if you want to replace it.',
          required: 'API Key Fingerprint is required'
        },
        ociTenancy: {
          label: 'Tenancy OCID',
          placeholder: 'ocid1.tenancy.oc1..aaaa...',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode: 'A tenancy OCID is already saved. Enter a new one only if you want to replace it.',
          required: 'Tenancy OCID is required'
        },
        ociKey: {
          label: 'Private Key (PEM content)',
          placeholder: '-----BEGIN PRIVATE KEY-----...-----END PRIVATE KEY-----',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode: 'A private key is already saved. Enter a new one only if you want to replace it.',
          required: 'Private Key is required'
        },
        ociRegion: {
          label: 'Region',
          placeholder: 'e.g., us-ashburn-1',
          required: 'Region is required'
        }
      },
      azure: {
        apiBase: {
          label: 'Endpoint',
          placeholder: 'e.g., https://[resource].openai.azure.com/',
          required: 'Endpoint is required',
          invalid: 'Invalid URL format. Must be a valid HTTP or HTTPS URL'
        },
        apiKey: {
          label: 'API Key',
          placeholder: 'Enter your API Key',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode: 'An API key is already saved. Enter a new one only if you want to replace it.',
          required: 'API Key is required'
        },
        apiVersion: {
          label: 'API Version',
          placeholder: 'e.g., 2024-02-15-preview'
        }
      },
      custom: {
        apiKey: {
          label: 'API Key',
          placeholder: 'Enter your API Key',
          placeholderEditMode: '••••••••••••••••',
          helperTextEditMode: 'An API key is already saved. Enter a new one only if you want to replace it.',
          required: 'API Key is required'
        },
        baseUrl: {
          label: 'Base URL',
          placeholder: 'e.g., https://api.yourprovider.com/v1',
          required: 'Base URL is required',
          invalid: 'Invalid URL format. Must be a valid HTTP or HTTPS URL',
          helperText: 'The base URL for your OpenAI-compatible API endpoint'
        }
      },
      models: {
        required: 'At least one model must be selected',
        loading: 'Loading models...',
        error: 'Error loading models',
        empty: 'No models available',
        placeholder: 'Select a model...',
        manualHelperText: 'Enter comma-separated model names (e.g., model-1, model-2)',
        custom: {
          label: 'Custom models',
          placeholder: 'model-1, model-2, model-3',
          helper: 'Enter comma-separated model IDs (e.g., model-a, model-b)'
        }
      },
      model: {
        label: 'Model',
        allSelected: 'All selected',
        selectAll: 'Select all',
        noneSelected: '0 selected',
        multipleSelected: '{count} selected'
      },
      buttons: {
        cancel: 'Cancel',
        save: 'Save'
      }
    },
    deleteDialog: {
      title: 'Delete configuration',
      description:
        'Are you sure you want to delete this configuration? This configuration may be used by multiple CARTO AI capabilities. This action cannot be undone.',
      confirm: 'Yes, delete',
      success: 'Configuration deleted successfully'
    },
    quotaAlert:
      'Using CARTO AI features will consume additional usage quota. Once enabled, your CARTO representative will reach out to discuss details to ensure you get the best out of our new AI capabilities. You can track your usage quota in {actionLink}.',
    readOnlyAlert:
      "You have read-only access to your organization's CARTO AI configuration. Contact an organization administrator to make changes.",
    enabling: 'Enabling CARTO AI...',
    disabling: 'Disabling CARTO AI...'
  },
  aiAgents: {
    title: 'CARTO AI in Builder',
    description:
      'Transform your Builder maps with CARTO AI Agents for a more engaging experience.\n\nEditors can enable these Agents, allowing Viewers to have a conversation with the map, making data exploration intuitive and interactive.',
    link: 'Learn more about AI Agents in our Documentation',
    control: 'Enable CARTO AI in Builder',
    controlHelper: 'By enabling CARTO AI, you agree to the {actionLink}',
    conditions: 'CARTO AI terms and conditions.'
  }
}
